'use client';

import React, { useState } from 'react';
import Link from 'next/link';

export default function HomeContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    company: '',
    phone: '',
    interest: 'suiting',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section className="bg-background py-24 px-6 md:px-12 border-t border-border">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Left */}
        <div className="flex flex-col justify-between gap-10">
          <div>
            <div className="section-label mb-6">Get in Touch</div>
            <h2 className="font-display text-[clamp(2rem,4vw,3rem)] text-foreground leading-tight tracking-tight mb-6">
              Start Your
              <br />
              <span className="italic text-muted-foreground font-light">Fabric Inquiry.</span>
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-sm">
              Submit your requirements and our team will get back to you within 24 hours with
              availability, pricing, and swatches.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { name: 'Mohit Chhallani', phone: '9321145843' },
              { name: 'Lokesh Chhallani', phone: '8080138137' },
              { name: 'Amit Gulgulia', phone: '7498159529' },
              { name: 'Ankit Jain', phone: '9887799066' },
            ].map((contact) => (
              <a
                key={contact.name}
                href={`tel:+91${contact.phone}`}
                className="border border-border p-5 hover:border-primary hover:-translate-y-0.5 transition-all duration-300 group"
              >
                <p className="font-semibold text-foreground text-sm group-hover:text-primary transition-colors">
                  {contact.name}
                </p>
                <p className="text-muted-foreground text-xs mt-1">+91 {contact.phone}</p>
              </a>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Link href="/contact" className="btn-outline text-sm">
              Full Contact Page
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
            <Link href="/contact" className="btn-primary text-sm">
              Contact for Bulk Order
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Right - Form */}
        <div className="border border-border bg-card p-8 md:p-12">
          {submitted ? (
            <div className="flex flex-col items-center justify-center h-full gap-6 text-center py-16">
              <div className="w-16 h-16 bg-primary/10 flex items-center justify-center">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <h3 className="font-display text-2xl text-foreground">Inquiry Received</h3>
              <p className="text-muted-foreground text-sm">
                Our team will contact you within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label className="form-label">Your Name</label>
                <input
                  name="name"
                  type="text"
                  placeholder="Full name"
                  className="form-input"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="form-label">Company / Business Name</label>
                <input
                  name="company"
                  type="text"
                  placeholder="Your company"
                  className="form-input"
                  value={form.company}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="form-label">Phone Number</label>
                <input
                  name="phone"
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                  className="form-input"
                  value={form.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="form-label">Fabric Interest</label>
                <select
                  name="interest"
                  className="form-input"
                  value={form.interest}
                  onChange={handleChange}
                >
                  <option value="suiting">Suiting Fabrics</option>
                  <option value="shirting">Shirting Fabrics</option>
                  <option value="both">Both Suiting & Shirting</option>
                </select>
              </div>
              <div>
                <label className="form-label">Message / Requirements</label>
                <textarea
                  name="message"
                  rows={3}
                  placeholder="Quantity, composition, delivery timeline..."
                  className="form-input resize-none"
                  value={form.message}
                  onChange={handleChange}
                />
              </div>
              <button type="submit" className="btn-primary w-full justify-center">
                Submit Inquiry
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
